import { useEffect, useState } from "react";
import { Receipt, Lock, Eye } from "lucide-react";
import { EmployeeLayout } from "@/components/EmployeeLayout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail, apiErrorCode } from "@/lib/api";
import { ReceiptPreviewDialog } from "@/components/ReceiptPreviewDialog";
import { EMPLOYEE_RECEIPTS } from "@/constants/testIds";

export default function EmployeeReceipts() {
  const { user } = useAuth();
  const [receipts, setReceipts] = useState(null);
  const [blocked, setBlocked] = useState(null);
  const [previewReceipt, setPreviewReceipt] = useState(null);

  useEffect(() => {
    if (!user?.employee_id) return;
    api.get(`/employees/${user.employee_id}/receipts`)
      .then(({ data }) => setReceipts(data))
      .catch((err) => {
        if (apiErrorCode(err) === "POLICIES_BLOCKING") {
          setBlocked(err.response.data.detail.message);
        } else {
          setBlocked(formatApiErrorDetail(err.response?.data?.detail));
        }
      });
  }, [user]);

  return (
    <EmployeeLayout>
      <h1 className="font-heading text-2xl font-bold text-charcoal mb-1">Mis recibos</h1>
      <p className="text-sm text-charcoal/60 mb-5">Historial de recibos de nómina.</p>

      {blocked && (
        <div data-testid={EMPLOYEE_RECEIPTS.blockedBanner} className="bg-accent border border-terracotta/30 rounded-2xl p-5 flex items-start gap-3">
          <Lock className="w-5 h-5 text-terracotta shrink-0 mt-0.5" strokeWidth={1.5} />
          <p className="text-sm text-charcoal/80">{blocked}</p>
        </div>
      )}

      {!blocked && receipts?.map((r) => (
        <div key={r.id} data-testid={EMPLOYEE_RECEIPTS.receiptRow} className="bg-card border border-petroleum/10 rounded-2xl p-4 mb-2 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Receipt className="w-4 h-4 text-petroleum" strokeWidth={1.5} />
            <div>
              <p className="text-sm font-medium text-charcoal">{r.period_label}</p>
              <p className="text-xs text-charcoal/50">{r.issue_date}</p>
            </div>
          </div>
          {r.has_file && (
            <button data-testid={EMPLOYEE_RECEIPTS.previewButton} onClick={() => setPreviewReceipt(r)} className="p-2 rounded-full bg-accent text-petroleum shrink-0">
              <Eye className="w-4 h-4" strokeWidth={1.5} />
            </button>
          )}
        </div>
      ))}
      {!blocked && receipts?.length === 0 && (
        <div className="bg-card border border-petroleum/10 rounded-2xl p-6 text-center">
          <Receipt className="w-6 h-6 text-charcoal/30 mx-auto mb-2" strokeWidth={1.5} />
          <p className="text-sm text-charcoal/50">Aún no tienes recibos registrados.</p>
          <p className="text-xs text-charcoal/40 mt-1">Aquí verás tus recibos de nómina en PDF cada vez que tu empleador los cargue, uno por cada periodo de pago.</p>
        </div>
      )}

      <ReceiptPreviewDialog
        employeeId={user?.employee_id}
        receipt={previewReceipt}
        open={!!previewReceipt}
        onOpenChange={(v) => !v && setPreviewReceipt(null)}
      />
    </EmployeeLayout>
  );
}
